import React from 'react';
import { motion } from 'framer-motion';
import { ProductNavbar } from '../ProductNavbar';
import { Wave } from '../Wave';
import { Stethoscope } from 'lucide-react';
interface ProductLayoutProps {
  children: React.ReactNode;
  product: {
    name: string;
    color: string;
    gradientFrom: string;
    gradientTo: string;
    description: string;
    links: Array<{
      label: string;
      path: string;
    }>;
    externalLink?: {
      label: string;
      url: string;
    };
  };
}
export const ProductLayout = ({
  children,
  product
}: ProductLayoutProps) => {
  // Helper function to get gradient colors based on product color
  const getGradientColors = () => {
    switch (product.color) {
      case 'teal':
        return 'from-teal-800 via-teal-700 to-teal-600';
      case 'orange':
        return 'from-orange-800 via-orange-700 to-orange-600';
      case 'red':
        return 'from-red-800 via-red-700 to-red-600';
      default:
        return 'from-blue-800 via-blue-700 to-blue-600';
    }
  };
  const getBaseColor = () => {
    switch (product.color) {
      case 'teal':
        return 'bg-teal-600';
      case 'orange':
        return 'bg-orange-600';
      case 'red':
        return 'bg-red-600';
      default:
        return 'bg-blue-600';
    }
  };
  return <div className="w-full min-h-screen bg-gray-50">
      <ProductNavbar color={product.color} product={product.name} links={product.links} externalLink={product.externalLink} />
      {/* Hero Section */}
      <section className={`relative h-[80vh] overflow-hidden ${getBaseColor()}`}>
        <div className="absolute inset-0">
          <div className={`absolute inset-0 bg-gradient-to-b ${getGradientColors()}`}>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTQuMDggMzBjMCAxMy4yNTUtMTAuNzQ1IDI0LTI0IDI0UzYuMDggNDMuMjU1IDYuMDggMzAgMTYuODI1IDYgMzAuMDggNnMyNCAxMC43NDUgMjQgMjR6IiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIgc3Ryb2tlLXdpZHRoPSIuNSIvPjwvc3ZnPg==')] opacity-10" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
          </div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="mb-8">
              {product.name === 'QuickCont' ? <img src="/quickcont-logo-white.png" alt="QuickCont" className="w-32 h-32" /> : product.name === 'Facelec+' ? <img src="/facelec-logo-white.png" alt="Facelec+" className="w-32 h-32" /> : <Stethoscope className="w-32 h-32 text-white" />}
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="text-5xl font-bold mb-6">
              {product.name}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} className="text-xl mb-8 max-w-2xl text-white/90">
              {product.description.includes('IPSE IA') ? product.description : product.name === 'QuickCont' ? 'Sistema ERP integral que unifica contabilidad, facturación, inventario, nómina y más en una sola plataforma. Potenciado por IPSE IA, nuestro asistente inteligente que automatiza tareas, detecta patrones y optimiza la gestión completa de tu empresa.' : product.description}
            </motion.p>
          </div>
        </motion.div>
      </section>
      {/* Main Content */}
      <main className="relative">{children}</main>
    </div>;
};